<script setup lang="ts">
import { performanceDefinitionMap } from '#leaderboards/utils/performance'
import { Card } from '@forgd/ui-kit/card'

type PerformanceMetrics = Pick<
  SuccessResponseBody<'/market-makers-leaderboard/performance/{marketMakerId}'>,
  'tradingKpis' | 'trustIntegration' | 'coverageCapabilities' | 'uptime' | 'integrationLevel' | 'composite'
>

const props = defineProps<{
  data: PerformanceMetrics
}>()

const METRIC_KEYS = ['composite', 'tradingKpis', 'trustIntegration', 'coverageCapabilities', 'uptime', 'integrationLevel'] as const

const METRIC_LABELS: Record<(typeof METRIC_KEYS)[number], string> = {
  composite: performanceDefinitionMap.composite.term,
  tradingKpis: performanceDefinitionMap.tradingKpis.term,
  trustIntegration: performanceDefinitionMap.trust.term,
  coverageCapabilities: performanceDefinitionMap.coverageCapabilities.term,
  uptime: performanceDefinitionMap.uptime.term,
  integrationLevel: performanceDefinitionMap.integrationLevel.term,
}

const RADAR_LABELS: Record<string, string | string[]> = {
  ...METRIC_LABELS,
  integrationLevel: ['Integration', 'Level'],
}

const radarMetrics = computed<Record<string, number | null>>(() => {
  return Object.fromEntries(METRIC_KEYS.map(k => [k, props.data[k]]))
})

function formatScore(value: number | null): string {
  if (value == null) {
    return 'N/A'
  }
  return `${value.toFixed(2)}/10.00`
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="w-full h-[378px]">
      <ChartRadar :metrics="radarMetrics" :labels="RADAR_LABELS" />
    </div>

    <Card>
      <div
        v-for="key in METRIC_KEYS"
        :key="key"
        class="flex items-center justify-between px-2 py-2"
      >
        <span class="text-xs text-primal">
          {{ METRIC_LABELS[key] }}
        </span>
        <span class="text-xs tabular-nums text-numeric text-primal">
          {{ formatScore(data[key]) }}
        </span>
      </div>
    </Card>
  </div>
</template>
